import React, { useState, useEffect } from 'react';
import { RefreshCw, Trash2, Scale, XCircle } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import Loader from '../common/Loader';
import './Workers.css';

const Workers = () => {
  const { apiCall } = useApi();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalScaleValue, setGlobalScaleValue] = useState(1);
  const [workerScaleValues, setWorkerScaleValues] = useState({});
  const [revokeTaskId, setRevokeTaskId] = useState('');
  const [revokeTerminate, setRevokeTerminate] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/admin/workers/status');
      setStatus(data);
      if (data.workers?.length > 0) {
        setGlobalScaleValue(data.workers[0].concurrency || 1);
        const scaleValues = {};
        data.workers.forEach((w) => {
          scaleValues[w.name] = w.concurrency || 1;
        });
        setWorkerScaleValues(scaleValues);
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScaleAll = async () => {
    try {
      await apiCall('/admin/workers/scale', {
        method: 'POST',
        body: JSON.stringify({ concurrency: globalScaleValue }),
      });
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleScaleWorker = async (workerName) => {
    const concurrency = workerScaleValues[workerName];
    try {
      await apiCall('/admin/workers/scale', {
        method: 'POST',
        body: JSON.stringify({ concurrency, worker_name: workerName }),
      });
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm('Restart all worker pools?')) return;
    try {
      await apiCall('/admin/workers/restart', { method: 'POST' });
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePurge = async () => {
    if (!window.confirm('Purge all pending tasks?')) return;
    try {
      const result = await apiCall('/admin/workers/purge', { method: 'POST' });
      alert(result.message);
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevokeTask = async () => {
    const taskId = revokeTaskId.trim();
    if (!taskId) return;
    const verb = revokeTerminate ? 'force-kill' : 'revoke';
    if (!window.confirm(`${verb} task ${taskId}?`)) return;
    try {
      const result = await apiCall(
        `/admin/workers/tasks/${encodeURIComponent(taskId)}/revoke`,
        {
          method: 'POST',
          body: JSON.stringify({ terminate: revokeTerminate, signal: 'SIGTERM' }),
        },
      );
      alert(result.message);
      setRevokeTaskId('');
      await fetchStatus();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleWorkerScaleChange = (workerName, value) => {
    setWorkerScaleValues({ ...workerScaleValues, [workerName]: parseInt(value) || 1 });
  };

  if (loading) return <Loader />;

  return (
    <div className="workers-page">
      <div className="page-header">
        <h2 className="page-title">Workers</h2>
        <button onClick={fetchStatus} className="btn-icon">
          <RefreshCw className="icon" />
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-label">Total Workers</p>
          <p className="stat-value">{status?.total_workers || 0}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Active Tasks</p>
          <p className="stat-value stat-green">{status?.active_tasks || 0}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">Scheduled Tasks</p>
          <p className="stat-value stat-yellow">{status?.scheduled_tasks || 0}</p>
        </div>
      </div>

      <div className="control-card">
        <h3 className="card-title">Global Controls</h3>
        <div className="control-actions">
          <div className="scale-control">
            <label className="form-label">Scale All Workers (1-10)</label>
            <div className="scale-input-group">
              <input
                type="number"
                min="1"
                max="10"
                value={globalScaleValue}
                onChange={(e) => setGlobalScaleValue(parseInt(e.target.value) || 1)}
                className="form-input"
              />
              <button onClick={handleScaleAll} className="btn btn-primary btn-with-icon">
                <Scale className="btn-icon-sm" />
                Scale All
              </button>
            </div>
          </div>
          <button onClick={handleRestart} className="btn btn-warning btn-with-icon">
            <RefreshCw className="btn-icon-sm" />
            Restart All
          </button>
          <button onClick={handlePurge} className="btn btn-danger btn-with-icon">
            <Trash2 className="btn-icon-sm" />
            Purge Queue
          </button>
        </div>
        <div className="scale-control">
          <label className="form-label">Revoke Task by ID</label>
          <div className="scale-input-group">
            <input
              type="text"
              value={revokeTaskId}
              onChange={(e) => setRevokeTaskId(e.target.value)}
              placeholder="task id"
              className="form-input"
            />
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={revokeTerminate}
                onChange={(e) => setRevokeTerminate(e.target.checked)}
              />
              Terminate if running
            </label>
            <button
              onClick={handleRevokeTask}
              disabled={!revokeTaskId.trim()}
              className="btn btn-danger btn-with-icon"
            >
              <XCircle className="btn-icon-sm" />
              Revoke
            </button>
          </div>
        </div>
      </div>

      <div className="workers-card">
        <h3 className="card-title">Worker Details</h3>
        {status?.workers?.length > 0 ? (
          <div className="workers-list">
            {status.workers.map((worker, idx) => (
              <div key={idx} className="worker-item">
                <div className="worker-header">
                  <span className="worker-name">{worker.name}</span>
                  <span className="worker-status">Active</span>
                </div>
                <div className="worker-stats">
                  <div className="worker-stat">
                    <span className="stat-label-sm">Concurrency:</span>
                    <span className="stat-value-sm">{worker.concurrency}</span>
                  </div>
                  <div className="worker-stat">
                    <span className="stat-label-sm">Active:</span>
                    <span className="stat-value-sm">{worker.active_tasks}</span>
                  </div>
                  <div className="worker-stat">
                    <span className="stat-label-sm">Reserved:</span>
                    <span className="stat-value-sm">{worker.reserved_tasks}</span>
                  </div>
                </div>
                <div className="worker-controls">
                  <div className="scale-input-group">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={workerScaleValues[worker.name] ?? worker.concurrency}
                      onChange={(e) => handleWorkerScaleChange(worker.name, e.target.value)}
                      className="form-input form-input-sm"
                    />
                    <button
                      onClick={() => handleScaleWorker(worker.name)}
                      className="btn btn-primary btn-sm btn-with-icon"
                    >
                      <Scale className="btn-icon-sm" />
                      Scale
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-data">No workers available</p>
        )}
      </div>
    </div>
  );
};

export default Workers;
